import React from 'react';
import deepmerge from 'deepmerge';
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  type BoxProps,
  type ButtonProps,
  type LinkProps,
  type MenuProps,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleRight,
  faBars,
  faChartLineUp,
  faCircleUser,
  faCommentPen,
  faExternalLink,
  faGear,
  faMessageSmile,
  faSignOut,
  faUserGraduate,
  faUserSecret,
} from '@fortawesome/pro-regular-svg-icons';

import { Icon } from '../../atoms/Icon';

import { ColorModeSwitcher } from './ColorModeSwitcher';
import { EnrolyLogo } from './EnrolyLogo';
import { NavbarActionButtons } from './constants';

interface IActAsProps {
  options: string[];
  currentValue: string;
  onChange: (value: string) => void;
}

interface INavbarProps {
  subnav?: React.ReactNode;
  actionableButtonsStates?: IActionableButtonsStates;
  username?: string;
  uniName?: string;
  isAuthenticated?: boolean;
  hasThemeSwitcher?: boolean;
  onLogout?: () => void;
  actAs?: IActAsProps;
}

interface IActionableButtonsStates {
  [NavbarActionButtons.STUDENTS]: IActionableButtonProps;
  [NavbarActionButtons.INSIGHTS]: IActionableButtonProps;
  [NavbarActionButtons.SETTINGS]: IActionableButtonProps;
  [NavbarActionButtons.FEEDBACK]: IActionableButtonProps;
}

interface IActionableButtonProps {
  isVisible: boolean;
  isSelected?: boolean;
  href?: string;
  [index: string]: unknown;
}

const Navbar = (props: INavbarProps): React.ReactElement => {
  const {
    subnav,
    actionableButtonsStates,
    username,
    isAuthenticated,
    hasThemeSwitcher = true,
    onLogout,
    actAs,
  } = props;

  const defaultActionableStates = {
    [NavbarActionButtons.STUDENTS]: {
      isSelected: false,
      isVisible: false,
      href: typeof window !== 'undefined' && `/${sessionStorage.getItem(`cas-staff-uni-backUrl`) ?? ''}`,
    },
    [NavbarActionButtons.INSIGHTS]: {
      isSelected: false,
      isVisible: false,
      href: typeof window !== 'undefined' && `/insights/summary`,
    },
    [NavbarActionButtons.SETTINGS]: {
      isSelected: false,
      isVisible: false,
      href: typeof window !== 'undefined' && `/config`,
    },
    [NavbarActionButtons.FEEDBACK]: {
      isSelected: false,
      isVisible: false,
    },
  };

  const navButtonStates = deepmerge(defaultActionableStates, actionableButtonsStates ?? {});

  const studentsProps = navButtonStates[NavbarActionButtons.STUDENTS];
  const insightsProps = navButtonStates[NavbarActionButtons.INSIGHTS];
  const settingsProps = navButtonStates[NavbarActionButtons.SETTINGS];
  const feedbackProps = navButtonStates[NavbarActionButtons.FEEDBACK];

  // only show buttons if more than one visible
  const showActionableButtons =
    [studentsProps.isVisible, insightsProps.isVisible, settingsProps.isVisible].filter(
      (menuItemVisibility?: boolean) => !!menuItemVisibility,
    ).length > 1;

  // Determine if mobile version should be shown
  const isMobile = useBreakpointValue({ base: true, md: false });

  const LogoLink = ({ ...rest }: LinkProps): React.ReactElement => {
    return (
      <Link
        href={studentsProps?.href ?? '/'}
        data-selector={'logo-nav-link'}
        aria-label="Go to the home page"
        {...rest}
      >
        <EnrolyLogo id="logo" alt="Enroly logo" isLogomark={!isMobile} />
      </Link>
    );
  };

  const ActionableButtons = ({ ...rest }: BoxProps): React.ReactElement | null => {
    const variant: ButtonProps['variant'] = isMobile ? 'mobiletopnav' : 'topnav';

    if (showActionableButtons) {
      return (
        <Box {...rest}>
          {studentsProps.isVisible && (
            <Button
              as={'a'}
              id={`${NavbarActionButtons.STUDENTS}-nav-button`}
              variant={variant}
              isActive={studentsProps.isSelected}
              href={studentsProps.href}
              leftIcon={
                <Box className="nav-icon">
                  <FontAwesomeIcon icon={faUserGraduate} fixedWidth role="none" />
                </Box>
              }
            >
              {NavbarActionButtons.STUDENTS}
            </Button>
          )}

          {insightsProps.isVisible && (
            <Button
              as={'a'}
              id={`${NavbarActionButtons.INSIGHTS}-nav-button`}
              isActive={insightsProps.isSelected}
              variant={variant}
              href={insightsProps.href}
              leftIcon={
                <Box className="nav-icon">
                  <FontAwesomeIcon icon={faChartLineUp} fixedWidth role="none" />
                </Box>
              }
            >
              {NavbarActionButtons.INSIGHTS}
            </Button>
          )}

          {settingsProps.isVisible && (
            <Button
              as={'a'}
              id={`${NavbarActionButtons.SETTINGS}-nav-button`}
              variant={variant}
              isActive={settingsProps.isSelected}
              href={settingsProps.href}
              leftIcon={
                <Box className="nav-icon">
                  <FontAwesomeIcon icon={faGear} fixedWidth role="none" />
                </Box>
              }
            >
              {NavbarActionButtons.SETTINGS}
            </Button>
          )}
        </Box>
      );
    }
    return null;
  };

  const UserButtons = ({ ...rest }: BoxProps): React.ReactElement | null => {
    const variant: ButtonProps['variant'] = isMobile ? 'sidebar' : 'topnav';
    const size: ButtonProps['size'] = isMobile ? 'sm' : undefined;

    const offset: MenuProps['offset'] = isMobile ? undefined : [-12, -2];
    const placement: MenuProps['placement'] = isMobile ? 'auto' : 'right-end';

    const isActing = actAs != null && username !== actAs.currentValue;

    const ActingIcon = ({ ...actingIconProps }: BoxProps): React.ReactElement => (
      <Center
        borderRadius={'full'}
        bgColor={'yellow.400'}
        color={'yellow.800'}
        fontSize={'14px'}
        lineHeight={4}
        width={5}
        height={5}
        position={'absolute'}
        top={{ base: '8px', md: '-1px' }}
        left={{ base: '8px', md: '11px' }}
        {...actingIconProps}
      >
        <FontAwesomeIcon icon={faUserSecret} fixedWidth />
      </Center>
    );

    if (isAuthenticated) {
      return (
        <Box {...rest}>
          {feedbackProps.isVisible ? (
            <Menu placement={placement} offset={offset}>
              <MenuButton
                as={Button}
                variant={variant}
                size={size}
                leftIcon={
                  <Box className="nav-icon">
                    <FontAwesomeIcon icon={faMessageSmile} fixedWidth />
                  </Box>
                }
                rightIcon={<FontAwesomeIcon className="utility-icon" icon={faAngleRight} />}
              >
                {NavbarActionButtons.FEEDBACK}
              </MenuButton>

              <MenuList>
                <MenuItem
                  as="a"
                  href="https://enroly.typeform.com/feedback"
                  target="_blank"
                  key="feedback"
                  icon={<FontAwesomeIcon className="utility-icon" icon={faCommentPen} size="lg" fixedWidth />}
                >
                  Give feedback{' '}
                  <Icon icon={faExternalLink} ml={1} iconProps={{ title: 'Opens in a new window', size: 'sm' }} />
                </MenuItem>
              </MenuList>
            </Menu>
          ) : null}

          <Menu placement={placement} offset={offset}>
            <MenuButton
              data-testid="user-menu"
              data-selector="user-menu"
              as={Button}
              variant={variant}
              size={size}
              leftIcon={
                <Box className="nav-icon">
                  {isActing ? <ActingIcon /> : null}
                  <FontAwesomeIcon icon={faCircleUser} fixedWidth />
                </Box>
              }
              rightIcon={<FontAwesomeIcon className="utility-icon" icon={faAngleRight} />}
            >
              <Box
                overflow={'hidden'}
                textOverflow={'ellipsis'}
                px={!isMobile ? 1 : 0}
                maxWidth={!isMobile ? 'navbarWidth' : undefined}
              >
                {isActing ? actAs?.currentValue : username}
              </Box>
            </MenuButton>

            <MenuList>
              <Box color={'muted'} fontSize={'sm'} fontWeight={'medium'} px={4} py={2} maxWidth={'16rem'}>
                {isActing ? 'Acting ' : 'Logged in '}
                as {isActing ? actAs?.currentValue : username}
              </Box>

              {actAs && actAs.options.length > 1 && (
                <>
                  <MenuOptionGroup
                    type="radio"
                    value={actAs.currentValue}
                    onChange={value => actAs.onChange(value as string)}
                  >
                    {actAs.options.map(option => (
                      <MenuItemOption
                        key={option}
                        value={option}
                        pl={6} // So they align with the other icons in the menu
                      >
                        {option}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>

                  <MenuDivider />
                </>
              )}

              {hasThemeSwitcher && <ColorModeSwitcher />}

              <MenuItem
                data-testid="logout-button"
                data-selector="logout-button"
                key="logout"
                onClick={evt => {
                  evt.preventDefault();
                  onLogout?.();
                }}
                icon={<FontAwesomeIcon className="utility-icon" icon={faSignOut} size="lg" fixedWidth />}
              >
                Log out
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>
      );
    }
    return null;
  };

  // Used for mobile drawer
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef<HTMLButtonElement>(null);

  if (isMobile) {
    return (
      <Box
        as={'nav'}
        aria-label={'Main navigation'}
        data-selector={'top-nav'}
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        position={'fixed'}
        right={0}
        left={0}
        bottom={0}
        top={'auto'}
        height={20}
        px={4}
        bgColor={'whiteAlpha.500'}
        backdropFilter={'blur(5px)'}
        borderTopWidth={1}
        borderTopColor={'whiteAlpha.800'}
        boxShadow={'md'}
        zIndex={8}
        _dark={{
          bgColor: 'whiteAlpha.50',
          borderTopColor: 'whiteAlpha.100',
          height: '81px',
        }}
      >
        <Button
          ref={btnRef}
          variant={'solid'}
          size={'lg'}
          onClick={onOpen}
          leftIcon={<FontAwesomeIcon icon={faBars} fixedWidth />}
          borderRadius={'full'}
          height={'60px'}
        >
          <Box>Menu</Box>
        </Button>

        <Drawer isOpen={isOpen} placement="left" onClose={onClose} finalFocusRef={btnRef}>
          <DrawerOverlay />

          <DrawerContent maxWidth="13.5rem">
            <DrawerHeader px={'sidebarPx'} pt={4}>
              <LogoLink />
            </DrawerHeader>

            <DrawerBody className="nav-scrollbar" px={'sidebarPxSmall'}>
              <ActionableButtons pb={4} px={'sidebarPxSmall'} />

              {subnav}
            </DrawerBody>

            <DrawerFooter display={'block'} px={'sidebarPx'} py={1}>
              <UserButtons />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    );
  }

  // Medium screen and above version
  return (
    <Box
      as={'nav'}
      aria-label={'Main navigation'}
      data-selector={'top-nav'}
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      position={'fixed'}
      top={0}
      left={0}
      bottom={0}
      width={'navbarWidth'}
      bgColor={'blue.700'}
      zIndex={101}
      _dark={{
        bgColor: 'gray.800',
        borderRightWidth: 1,
      }}
    >
      <LogoLink
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        width={'100%'}
        height={'navbarWidth'}
      />

      <ActionableButtons />

      <UserButtons mb={20} mt={'auto'} />
    </Box>
  );
};

export { Navbar };
